/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { ROUTES } from '../../constants/routes';
import { RootState } from '../../reducers';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import { CustomReport, CustomReportMetric, CustomReportsValue, getMetricTypeLabel, MetricType } from '../../types/custom-report';
import Loader from '../loader';
import Table, { TableColumn } from '../table/Table';
import { CustomReportRoutes } from './CustomReportsRoot';
import IconButton from '../button/IconButton';
import CustomReportsMetricDetailsModal from './CustomReportsMetricDetailsModal';
import { UniversalTrackerValueView } from '../../utils/universalTrackerValueView';
import UniversalTracker from '../../model/UniversalTracker';
import SimpleTooltip from '../simple-tooltip';
import './styles.scss';

const getFormattedValue = (reportValue?: CustomReportsValue) => {
  if (!reportValue) {
    return <>-</>;
  }
  if (reportValue.customMetricType === MetricType.Text) {
    return <SimpleTooltip text='Can only be viewed in file download'>
      <i className='fa fa-info-circle' />
    </SimpleTooltip>;
  }
  const universalTracker = new UniversalTracker(reportValue.utr);
  return <UniversalTrackerValueView
    utr={universalTracker}
    utrv={reportValue.utrv}
    valueType={universalTracker.getValueType()}
  />
}

const columns: TableColumn[] = [
  {
    Header: 'Metric name',
    accessor: 'name',
  },
  {
    Header: 'Type',
    accessor: 'metricType',
  },
  {
    Header: 'Result',
    accessor: 'value',
  },
  {
    id: 'buttons',
    Header: '',
    accessor: 'buttons',
    cellProps: {
      className: 'text-center'
    },
    minWidth: 150,
    Cell: (c) => {
      const buttons = c.value;
      return (
        <div className='text-right'>
          <div className='d-inline mr-2 border-end' />
          <IconButton icon='fa-filter' disabled={!buttons.isCalculation} tooltip='View disaggregations' key={'op-details'} className={'mr-1'} onClick={buttons.detailsClick} />
          {/* <IconButton colour={1} icon='fa-eye' tooltip='View data history' key={`op-history`} className={`mr-3`} onClick={buttons.historyClick} /> */}
          <IconButton colour={5} icon='fa-trash' tooltip='Delete metric' key={'op-delete'} className={'mr-1'} onClick={buttons.deleteClick} />
        </div>
      );
    }
  },
];

interface CustomReportMetricsListProps {
  customReport?: CustomReport;
  handleReload: () => Promise<void>;
}

interface UtrvState {
  loaded: boolean;
  values: CustomReportsValue[];
}

const CustomReportMetricsList = (props: CustomReportMetricsListProps) => {
  const { customReport, handleReload } = props;

  const [metricsList, setMetricsList] = useState<CustomReportMetric[]>([]);
  const [isSaving, setSaving] = useState(false);
  const initiativeState = useSelector((state: RootState) => state.initiative);
  const [showDetails, setShowDetails] = useState<string | undefined>(undefined);

  const [utrvState, setUtrvState] = useState<UtrvState>({ loaded: false, values: [] });

  React.useEffect(() => {
    if (initiativeState.id && customReport?._id) {
      G17Client.getCustomReportValues(initiativeState.id, customReport?._id)
        .then(values => setUtrvState({ loaded: true, values: values }))
        .catch(() => setUtrvState({ loaded: true, values: [] }));
    }
  }, [initiativeState, customReport]);

  const history = useHistory();

  React.useEffect(() => {
    if (customReport?.metrics) {
      setMetricsList(customReport.metrics);
    }
  }, [customReport]);

  if (!initiativeState.loaded) {
    return <Loader />;
  }

  const initiativeId = initiativeState.id;

  const handleDelete = async (id: string) => {
    setSaving(true);
    if (!customReport) {
      return;
    }

    G17Client.customReportRemoveMetric(initiativeId, customReport._id, id)
      .then(() => handleReload())
      .then(() => {
        history.push(generateUrl(ROUTES.DOWNLOADS_CUSTOM, { initiativeId, reportId: customReport._id }));
        setSaving(false);
      })
      .catch(() => {
        setSaving(false);
      });
  }

  const handleGoToAdd = () => {
    if (!customReport) {
      return;
    }
    const url = generateUrl(CustomReportRoutes.ADD_METRIC, {
      initiativeId: customReport.initiativeId,
      reportId: customReport._id,
    });
    history.push(url);
  }

  const handleToggleDetails = (utrId?: string) => {
    setShowDetails((currentUtrId) => currentUtrId === utrId ? undefined : utrId)
  }

  return <>
    <Table columns={columns} data={
      !customReport ? [] : metricsList.map((metric) => {
        const value = utrvState.values.find(({ utr }) => utr._id === metric.utrId);
        const isCalculation = metric.type === MetricType.Calculated;
        return {
          ...metric,
          name: customReport.universalTrackers?.find(utr => utr._id === metric.utrId)?.name,
          metricType: getMetricTypeLabel(metric.type),
          value: <span className='pr-3'>{getFormattedValue(value)}</span>,
          buttons: {
            detailsClick: isCalculation ? () => handleToggleDetails(metric.utrId) : null,
            deleteClick: () => handleDelete(metric.utrId),
            isCalculation,
          }
        }
      })
    }
      noData='Use the button below to add metrics to this custom report' />
    <div className='text-center mt-4'>
      <Button disabled={isSaving || !customReport} outline={true} onClick={handleGoToAdd}>
        Add a new metric
      </Button>
    </div>
    {utrvState.loaded && showDetails && customReport
      ?
      <CustomReportsMetricDetailsModal customReport={customReport} metricValues={utrvState.values} utrId={showDetails} toggle={handleToggleDetails} />
      :
      <></>
    }

  </>
}

export default CustomReportMetricsList;
